import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    AppBar,
    Toolbar,
    Container
} from '@mui/material';
import {
    AccountCircle as AccountCircleIcon,
    Groups as GroupsIcon,
    Business as BusinessIcon,
    Star as StarIcon,
    Favorite as FavoriteIcon,
    Security as SecurityIcon,
    Badge as BadgeIcon,
    CardMembership as CardMembershipIcon,
    Store as StoreIcon,
    StoreMallDirectory as StoreMallDirectoryIcon,
    Storefront as StorefrontIcon,
    LocationOn as LocationOnIcon,
    Handshake as HandshakeIcon,
    Assignment as AssignmentIcon,
    AccountBalance as AccountBalanceIcon,
    Domain as DomainIcon,
    // Tab icons
    List as ListIcon,
    Assessment as AssessmentIcon,
    TrendingUp as TrendingUpIcon,
    CreditCard as CreditCardIcon,
    Receipt as ReceiptIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    ShowChart as ShowChartIcon,
    ShoppingCart as ShoppingCartIcon,
    Payment as PaymentIcon,
    MonetizationOn as MonetizationOnIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    AttachMoney as AttachMoneyIcon,
    PointOfSale as PointOfSaleIcon,
    ConfirmationNumber as ConfirmationNumberIcon,
    LocalAtm as LocalAtmIcon,
    QrCode as QrCodeIcon,
    SmartToy as SmartToyIcon,
    AddCard as AddCardIcon,
    CardGiftcard as CardGiftcardIcon,
    Redeem as RedeemIcon,
} from '@mui/icons-material';
import PulseIcon from '../components/PulseIcon';
import { PlatformIcon, MemberCardIcon, NetworkIcon, TrustIcon, GeometricIcon } from '../components/AlternativeIcons';

const IconPreview = () => {
    const materialIcons = [
        { name: 'Store', component: StoreIcon, description: 'Classic store/franchise location icon' },
        { name: 'StoreMallDirectory', component: StoreMallDirectoryIcon, description: 'Shopping center/retail franchise' },
        { name: 'Storefront', component: StorefrontIcon, description: 'Modern storefront design' },
        { name: 'Handshake', component: HandshakeIcon, description: 'Partnership/franchise agreement' },
        { name: 'Domain', component: DomainIcon, description: 'Network of franchise locations' },
        { name: 'LocationOn', component: LocationOnIcon, description: 'Franchise location marker' },
        { name: 'Assignment', component: AssignmentIcon, description: 'Franchise documentation/contracts' },
        { name: 'AccountBalance', component: AccountBalanceIcon, description: 'Professional/corporate franchise' },
        { name: 'Business', component: BusinessIcon, description: 'Business/enterprise focused' },
        { name: 'Star', component: StarIcon, description: 'Premium/quality franchise' },
    ];

    const customIcons = [
        { name: 'Pulse (Current)', component: PulseIcon, description: 'Pulse/heartbeat design' },
        { name: 'Platform', component: PlatformIcon, description: 'Simple circle with "P" letter' },
        { name: 'MemberCard', component: MemberCardIcon, description: 'Membership card design' },
        { name: 'Network', component: NetworkIcon, description: 'Connected community nodes' },
        { name: 'Trust', component: TrustIcon, description: 'Shield with checkmark (security/trust)' },
        { name: 'Geometric', component: GeometricIcon, description: 'Modern hexagon with center dot' },
    ];

    const tabIcons = [
        // Giftcard List tab options
        { name: 'List', component: ListIcon, description: 'Giftcard List - Simple list view' },
        { name: 'CreditCard', component: CreditCardIcon, description: 'Giftcard List - Credit card icon' },
        { name: 'CardGiftcard', component: CardGiftcardIcon, description: 'Giftcard List - Gift card specific' },
        { name: 'ConfirmationNumber', component: ConfirmationNumberIcon, description: 'Giftcard List - Confirmation/ticket' },
        
        // Transaction Report tab options
        { name: 'Assessment', component: AssessmentIcon, description: 'Transaction Report - Assessment/report' },
        { name: 'Receipt', component: ReceiptIcon, description: 'Transaction Report - Receipt icon' },
        { name: 'BarChart', component: BarChartIcon, description: 'Transaction Report - Bar chart' },
        { name: 'ShowChart', component: ShowChartIcon, description: 'Transaction Report - Line chart' },
        { name: 'PointOfSale', component: PointOfSaleIcon, description: 'Transaction Report - POS system' },
        
        // Giftcard Sold Report tab options
        { name: 'TrendingUp', component: TrendingUpIcon, description: 'Giftcard Sold Report - Trending up' },
        { name: 'ShoppingCart', component: ShoppingCartIcon, description: 'Giftcard Sold Report - Shopping cart' },
        { name: 'Payment', component: PaymentIcon, description: 'Giftcard Sold Report - Payment icon' },
        { name: 'MonetizationOn', component: MonetizationOnIcon, description: 'Giftcard Sold Report - Money symbol' },
        { name: 'PieChart', component: PieChartIcon, description: 'Giftcard Sold Report - Pie chart' },
        
        // Activate Giftcard tab options
        { name: 'AddCard', component: AddCardIcon, description: 'Activate Giftcard - Add card icon' },
        { name: 'Redeem', component: RedeemIcon, description: 'Activate Giftcard - Redeem/activate' },
        { name: 'SmartToy', component: SmartToyIcon, description: 'Activate Giftcard - Smart activation' },
        { name: 'QrCode', component: QrCodeIcon, description: 'Activate Giftcard - QR code activation' },
        { name: 'LocalAtm', component: LocalAtmIcon, description: 'Activate Giftcard - ATM/kiosk activation' },
    ];

    const IconCard = ({ name, IconComponent, description, isTitle = false }) => (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                {isTitle && (
                    <Box sx={{ mb: 2 }}>
                        <AppBar position="static" sx={{ borderRadius: 1 }}>
                            <Toolbar sx={{ minHeight: '48px !important', justifyContent: 'center' }}>
                                <IconComponent sx={{ mr: 1, color: 'white' }} />
                                <Typography variant="h6" component="div" sx={{ fontSize: '0.9rem' }}>
                                    Resturant Operation Made Easy
                                </Typography>
                            </Toolbar>
                        </AppBar>
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <IconComponent sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                    {name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </CardContent>
        </Card>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
                Icon Preview - Choose Your Favorite
            </Typography>
            <Typography variant="body1" align="center" sx={{ mb: 4 }}>
                Here are all available icons. Each shows how it would look in the title bar.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
                Material-UI Built-in Icons
            </Typography>
            <Grid container spacing={3}>
                {materialIcons.map((icon) => (
                    <Grid xs={12} sm={6} md={4} lg={3} key={icon.name}>
                        <IconCard 
                            name={icon.name}
                            IconComponent={icon.component}
                            description={icon.description}
                            isTitle={true}
                        />
                    </Grid>
                ))}
            </Grid>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
                Custom Designed Icons
            </Typography>
            <Grid container spacing={3}>
                {customIcons.map((icon) => (
                    <Grid xs={12} sm={6} md={4} lg={3} key={icon.name}>
                        <IconCard 
                            name={icon.name}
                            IconComponent={icon.component}
                            description={icon.description}
                            isTitle={true}
                        />
                    </Grid>
                ))}
            </Grid>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
                Giftcard Management Tab Icons
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose icons for each of the 4 tabs: Giftcard List, Transaction Report, Giftcard Sold Report, and Activate Giftcard
            </Typography>
            <Grid container spacing={3}>
                {tabIcons.map((icon) => (
                    <Grid xs={12} sm={6} md={4} lg={3} key={icon.name}>
                        <IconCard 
                            name={icon.name}
                            IconComponent={icon.component}
                            description={icon.description}
                            isTitle={false}
                        />
                    </Grid>
                ))}
            </Grid>

            <Paper sx={{ mt: 4, p: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
                <Typography variant="h6" gutterBottom>
                    How to Choose:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    For the main platform icon: "I want the CardMembership icon" or "I like the Network icon"
                </Typography>
                <Typography variant="body2">
                    For tab icons, specify which tab and which icon: 
                    "For Giftcard List tab, I want the CreditCard icon" or 
                    "For Transaction Report tab, I want the Assessment icon"
                </Typography>
            </Paper>
        </Container>
    );
};

export default IconPreview;
